import {
  Fragment,
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from "react";
import Modal from "../elements/Modal";
import { lngDetect } from "../../helpers/lngDetect";
import Button from "../form/Button";
import RatingStars from "./RatingStars";
import { useQuery } from "@tanstack/react-query";
import { organizationReviewList } from "../../services/reviewService";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import OrganizationReviewModal from "./OrganizationReviewModal"; // Import the edit modal
import { t } from "i18next";
// import { useOutletContext } from "react-router-dom";
// import ReviewPreview from "./ReviewPreview";
// import Select from "../form/select/Select";

// Review Filter Options
// const SORT_OPTIONS = [
//   { name: "Most Recent", value: "-createdAt" },
//   { name: "Oldest First", value: "-createdAt" },
//   { name: "Highest Rating", value: "-rating" },
//   { name: "Lowest Rating", value: "rating" },
// ];

// const RATING_FILTER_OPTIONS = [
//   { name: "All Ratings", value: "" },
//   { name: "5 Stars", value: "5" },
//   { name: "4 Stars", value: "4" },
//   { name: "3 Stars", value: "3" },
//   { name: "2 Stars", value: "2" },
//   { name: "1 Star", value: "1" },
// ];

// Enhanced Review Card Component
const DetailedReviewCard = ({ review, currentUser, onEditReview }) => {
  const isOwner = currentUser && review.userId === currentUser._id;

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 md:p-6 mb-4 hover:shadow-md transition-shadow duration-200">
      <div className="flex items-start gap-4">
        {/* Avatar */}
        <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full flex-shrink-0 flex items-center justify-center shadow-sm">
          {review.userAvatar ? (
            <img
              src={review.userAvatar}
              alt={review.userName}
              className="w-full h-full rounded-full object-cover"
            />
          ) : (
            <span className="text-white text-sm font-semibold">
              {(review.userName || "A").charAt(0).toUpperCase()}
            </span>
          )}
        </div>

        <div className="flex-1 min-w-0">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <span className="font-semibold text-gray-900">
                {review.userName || "Anonymous"}
              </span>
              {review.isVerified && (
                <div className="flex items-center justify-center w-5 h-5 bg-green-500 rounded-full">
                  <i className="fa-solid fa-check text-white text-xs"></i>
                </div>
              )}
              {review.wouldRecommend && (
                <div className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                  Recommends
                </div>
              )}
              {isOwner && (
                <div className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
                  Your Review
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <div className="flex gap-3 flex-row md:flex-col">
                <div className="text-xs text-gray-500">
                  {dayjs(review.date).format("MMM DD, YYYY")}
                </div>
                <div className="text-xs text-gray-500">
                  {dayjs(review.date).format("hh:mm A") || ""}
                </div>
              </div>
              {isOwner && (
                <button
                  onClick={() => onEditReview(review)}
                  className="ml-2 text-blue-600 hover:text-blue-800 transition-colors duration-200 p-1 rounded hover:bg-blue-50"
                  title="Edit your review"
                >
                  <i className="fa-solid fa-edit text-sm"></i>
                </button>
              )}
            </div>
          </div>

          {/* Rating */}
          <div className="mb-3">
            <RatingStars rating={review.rating} showCount={false} size="sm" />
          </div>

          {/* Review Text */}
          <p className="text-gray-700 text-sm leading-relaxed mb-4">
            {review.comment}
          </p>
        </div>
      </div>
    </div>
  );
};

const AllReviewsModal = ({
  isOpen,
  onClose = () => {},
  organizationData,
  // initialRating = 0,
  // initialTotalReviews = 0,
}) => {
  const [filters, setFilters] = useState({
    limit: 10,
    page: 1,
    sortQuery: "-createdAt",
    ratingFilter: "",
  });

  const [allReviews, setAllReviews] = useState([]);
  const [hasLoadedInitial, setHasLoadedInitial] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingReview, setEditingReview] = useState(null);
  // const [currentAverageRating, setCurrentAverageRating] =
  //   useState(initialRating);

  const scrollRef = useRef(null);
  const observerRef = useRef(null);

  // Get current user data
  const { data: currentUser } = useSelector((state) => state?.profile);

  // Fetch reviews with pagination
  const {
    data: reviewData,
    isLoading: reviewsLoading,
    isFetching: reviewsFetching,
    error: reviewError,
    refetch: refetchReviews,
  } = useQuery({
    queryKey: ["all-reviews", filters, organizationData?.data?._id],
    queryFn: async () => {
      if (!organizationData?.data?._id) {
        throw new Error("Organization ID is required");
      }

      const queryParams = {
        ...filters,
        referenceId: organizationData?.data._id,
        status: ["approved", "pending"],
      };

      // Add rating filter if selected
      if (filters.ratingFilter) {
        queryParams.rating = filters.ratingFilter;
      }

      const res = await organizationReviewList(queryParams);

      if (res?.docs?.status !== 200) {
        throw new Error(res?.message || "Failed to fetch reviews");
      }

      return res?.docs;
    },
    enabled: !!organizationData?.data?._id && isOpen,
    keepPreviousData: true,
    retry: 2,
    onError: (error) => {
      console.error("Review fetch error:", error);
      toast.error(error?.message || "Failed to load reviews");
    },
  });

  // Format and accumulate reviews
  const formattedReviews = useMemo(() => {
    if (!reviewData?.data || !Array.isArray(reviewData.data)) {
      return [];
    }

    return reviewData.data
      .map((item) => ({
        id: item._id || `review-${Math.random()}`, // Fallback ID
        userId: item?.userData?._id, // Add user ID for ownership check
        userName: item.isAnonymous
          ? "Anonymous"
          : `${item?.userData?.firstName ?? ""} ${
              item?.userData?.lastName ?? ""
            }`.trim() || "Anonymous",
        rating: Number(item?.overallRating || item?.rating || 0),
        comment: item?.review || item?.message || "No comment provided",
        date: item?.createdAt || new Date().toISOString(),
        isVerified: Boolean(item?.userData?.isVerified),
        isAnonymous: Boolean(item?.isAnonymous),
        categoryRatings: item?.categoryRatings || {},
        wouldRecommend: Boolean(item?.wouldRecommend),
        userAvatar: item?.userData?.avatar?.url || null,
        originalData: item, // Keep original data for editing
      }))
      .filter((review) => review.id); // Filter out reviews without valid IDs
  }, [reviewData]);

  // Handle edit review
  const handleEditReview = useCallback((review) => {
    setEditingReview(review);
    setEditModalOpen(true);
  }, []);

  // Handle edit modal close
  const handleEditModalClose = useCallback(() => {
    setEditModalOpen(false);
    setEditingReview(null);
  }, []);

  // Handle successful review update
  const handleReviewUpdateSuccess = useCallback(() => {
    // Refresh the reviews list
    refetchReviews();

    // Reset pagination to show updated review
    setFilters((prev) => ({ ...prev, page: 1 }));
    setAllReviews([]);
    setHasLoadedInitial(false);

    // Call parent callback
    organizationData.organizationRefetch();

    // Close edit modal
    handleEditModalClose();
  }, [refetchReviews]);

  // Handle pagination - accumulate reviews
  useEffect(() => {
    if (formattedReviews.length > 0) {
      if (filters.page === 1 || !hasLoadedInitial) {
        setAllReviews(formattedReviews);
        setHasLoadedInitial(true);
      } else {
        // Subsequent pages - append reviews
        setAllReviews((prev) => {
          const existingIds = new Set(prev.map((review) => review.id));
          const newReviews = formattedReviews.filter(
            (newReview) => !existingIds.has(newReview.id)
          );
          return [...prev, ...newReviews];
        });
      }
    }
  }, [formattedReviews, filters.page, hasLoadedInitial]);

  // Calculate rating statistics
  // const ratingStats = useMemo(() => {
  //   // Use allReviews data if we have loaded reviews
  //   if (allReviews && allReviews.length > 0) {
  //     const validReviews = allReviews.filter((review) => review.rating > 0);
  //     const totalRating = validReviews.reduce(
  //       (sum, review) => sum + review.rating,
  //       0
  //     );
  //     const averageRating =
  //       validReviews.length > 0 && hasUpdatedReviews
  //         ? totalRating / validReviews.length
  //         : currentAverageRating;

  //     const ratingBreakdown = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
  //     validReviews.forEach((review) => {
  //       const roundedRating = Math.round(review.rating);
  //       if (roundedRating >= 1 && roundedRating <= 5) {
  //         ratingBreakdown[roundedRating]++;
  //       }
  //     });

  //     const recommendationPercentage =
  //       validReviews.length > 0
  //         ? Math.round(
  //             (validReviews.filter((r) => r.wouldRecommend).length /
  //               validReviews.length) *
  //               100
  //           )
  //         : 0;

  //     return {
  //       averageRating,
  //       totalReviews: reviewData?.metadata?.totalDocs || validReviews.length,
  //       ratingBreakdown,
  //       recommendationPercentage,
  //     };
  //   }

  //   return {
  //     averageRating: initialRating,
  //     totalReviews: initialTotalReviews,
  //     ratingBreakdown: { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 },
  //     recommendationPercentage: 0,
  //   };
  // }, [
  //   allReviews,
  //   reviewData?.metadata?.totalDocs,
  //   initialRating,
  //   initialTotalReviews,
  // ]);

  // Infinite scroll observer
  const lastReviewElementRef = useCallback(
    (node) => {
      if (reviewsLoading || reviewsFetching) return;
      if (observerRef.current) observerRef.current.disconnect();

      observerRef.current = new IntersectionObserver(
        (entries) => {
          if (
            entries[0].isIntersecting &&
            reviewData?.metadata?.hasNextPage &&
            !reviewsFetching
          ) {
            setFilters((prev) => ({ ...prev, page: prev.page + 1 }));
          }
        },
        {
          threshold: 0.1,
        }
      );

      if (node) observerRef.current.observe(node);
    },
    [reviewsLoading, reviewsFetching, reviewData?.metadata?.hasNextPage]
  );

  // Handle filter changes
  // const handleSortChange = useCallback((sortValue) => {
  //   setFilters((prev) => ({ ...prev, sortQuery: sortValue, page: 1 }));
  //   setAllReviews([]);
  //   setHasLoadedInitial(false);
  // }, []);

  // const handleRatingFilterChange = useCallback((ratingValue) => {
  //   setFilters((prev) => ({ ...prev, ratingFilter: ratingValue, page: 1 }));
  //   setAllReviews([]);
  //   setHasLoadedInitial(false);
  // }, []);

  // Reset state when modal closes
  const handleClose = useCallback(() => {
    setAllReviews([]);
    setHasLoadedInitial(false);
    setFilters({
      limit: 10,
      page: 1,
      sortQuery: "-createdAt",
      ratingFilter: "",
    });

    // Disconnect observer
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    // Close edit modal if open
    if (editModalOpen) {
      handleEditModalClose();
    }

    onClose();
  }, [onClose, editModalOpen, handleEditModalClose]);

  // Cleanup observer on unmount
  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  const organizationName = useMemo(() => {
    return (
      lngDetect(organizationData?.data?.name) ||
      lngDetect(organizationData?.data?.eventSetting?.title) ||
      "Organization"
    );
  }, [organizationData?.data]);

  return (
    <Fragment>
      <Modal
        isOpen={isOpen}
        setIsOpen={handleClose}
        title={`${t("Reviews for")} ${organizationName}`}
        size="xl4"
        titleIcon="fa-regular fa-star"
        titleClass="!font-semibold !text-xl !capitalize truncate max-w-[700px] w-full tracking-tight !text-deep-purple"
        headerClass="!text-black !rounded-t-2xl border-b border-surface-03 bg-primary/10 py-4 px-10"
        childrenClasses="!bg-gray-50"
        bodyClasses={"!py-0 !px-0"}
      >
        <div className="max-w-5xl mx-auto h-[80vh] flex flex-col">
          {/* Reviews List */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-8 py-6">
            {/* Error State */}
            {reviewError && (
              <div className="text-center py-12">
                <div className="text-red-400 text-6xl mb-4">
                  <i className="fa-regular fa-exclamation-triangle"></i>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Failed to load reviews
                </h3>
                <p className="text-gray-600 mb-4">
                  {reviewError.message ||
                    "Something went wrong while loading reviews."}
                </p>
                <Button
                  buttonLabel="Try Again"
                  buttonFunction={() => refetchReviews()}
                  buttonClasses="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                />
              </div>
            )}

            {/* Initial Loading */}
            {reviewsLoading && !hasLoadedInitial && !reviewError && (
              <div className="space-y-4">
                {[...Array(3)].map((_, index) => (
                  <div
                    key={index}
                    className="bg-white rounded-xl border border-gray-200 p-6 animate-pulse"
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-gray-200 rounded-full"></div>
                      <div className="flex-1">
                        <div className="h-4 bg-gray-200 rounded w-1/4 mb-2"></div>
                        <div className="h-3 bg-gray-200 rounded w-1/6 mb-3"></div>
                        <div className="h-3 bg-gray-200 rounded w-full mb-2"></div>
                        <div className="h-3 bg-gray-200 rounded w-3/4"></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Reviews List */}
            {!reviewError && allReviews.length > 0 && (
              <div className="space-y-4">
                {allReviews.map((review, index) => (
                  <div
                    key={review.id}
                    ref={
                      index === allReviews.length - 1
                        ? lastReviewElementRef
                        : null
                    }
                  >
                    <DetailedReviewCard
                      review={review}
                      currentUser={currentUser}
                      onEditReview={handleEditReview}
                    />
                  </div>
                ))}

                {/* Loading more indicator */}
                {reviewsFetching && filters.page > 1 && (
                  <div className="text-center py-4">
                    <div className="inline-flex items-center gap-2 text-gray-600">
                      <i className="fa-solid fa-spinner fa-spin"></i>
                      Loading more reviews...
                    </div>
                  </div>
                )}

                {/* End of results */}
                {!reviewData?.metadata?.hasNextPage &&
                  allReviews.length > 0 && (
                    <div className="text-center py-4 text-gray-500">
                      <i className="fa-regular fa-check-circle text-lg mb-2 block"></i>
                      <div>{t("You've seen all reviews")}</div>
                    </div>
                  )}
              </div>
            )}

            {/* No reviews found */}
            {!reviewError &&
              !reviewsLoading &&
              allReviews.length === 0 &&
              hasLoadedInitial && (
                <div className="text-center py-12">
                  <div className="text-gray-400 text-6xl mb-4">
                    <i className="fa-regular fa-comment-dots"></i>
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    No reviews found
                  </h3>
                  <p className="text-gray-600">
                    {filters.ratingFilter
                      ? `No ${filters.ratingFilter}-star reviews found. Try adjusting your filters.`
                      : "Be the first to leave a review for this organizer!"}
                  </p>
                </div>
              )}
          </div>
        </div>
      </Modal>

      {/* Edit Review Modal */}
      <OrganizationReviewModal
        isOpen={editModalOpen}
        onClose={handleEditModalClose}
        organizationData={organizationData?.data}
        onSuccess={handleReviewUpdateSuccess}
        editData={editingReview}
        isEdit={true}
      />
    </Fragment>
  );
};

export default AllReviewsModal;
